import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SystemConfigService } from '@app/services/system-config.service';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  deleteSystemConfig,
  getChatChannels,
  getChatChannelsError,
  getChatChannelsSuccess,
  getSystemConfig,
  getSystemConfigError,
  getSystemConfigSuccess,
  getTimerReport,
  getTimerReportError,
  getTimerReportSuccess,
  setPropertyChatChannels,
  setPropertyChatChannelsError,
  setPropertyChatChannelsSuccess,
  updateSystemConfig,
  updateSystemConfigError,
  updateSystemConfigSuccess,
} from './system-configuration.actions';

@Injectable()
export class SystemConfigurationEffects {
  getSystemConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSystemConfig),
      switchMap(() =>
        this.systemConfigService.getSystemConfig().pipe(
          map(config => getSystemConfigSuccess({ systemConfig: config })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorRetrievingSystemConfig);
            return of(getSystemConfigError());
          })
        )
      )
    )
  );

  deleteSystemConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteSystemConfig),
      switchMap(action =>
        this.systemConfigService.deleteSystemConfig(action.id).pipe(
          mergeMap(config => [getSystemConfig()]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorUpdatingSystemConfig);
            return of(updateSystemConfigError());
          })
        )
      )
    )
  );

  updateSystemConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateSystemConfig),
      switchMap(action =>
        (action.pmsCredential.id > 0
          ? this.systemConfigService.updateSystemConfig(action.pmsCredential)
          : this.systemConfigService.addSystemConfig(action.pmsCredential)
        ).pipe(
          mergeMap(config => [updateSystemConfigSuccess(), getSystemConfig()]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorUpdatingSystemConfig);
            return of(updateSystemConfigError());
          })
        )
      )
    )
  );

  getTimerReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTimerReport),
      switchMap(() =>
        this.systemConfigService.getTimerReport().pipe(
          map(timerReport => getTimerReportSuccess({ timerReport })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorRetrievingTimerReport);
            return of(getTimerReportError());
          })
        )
      )
    )
  );

  getChatChannels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getChatChannels),
      switchMap(() =>
        this.systemConfigService.getChatHooksChannels().pipe(
          map(channels => getChatChannelsSuccess({ channels })),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorRetrievingSlackChannels);
            return of(getChatChannelsError());
          })
        )
      )
    )
  );

  setPropertyChatChannels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setPropertyChatChannels),
      switchMap(action =>
        this.systemConfigService.setPropertyChatChannel(action.propertyId, action.channelIds).pipe(
          map(channels => {
            return setPropertyChatChannelsSuccess();
          }),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorUpdatingSlackChannel);
            return of(setPropertyChatChannelsError());
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private systemConfigService: SystemConfigService,
    private snackbarService: SnackbarService
  ) {}
}
